import { isPlatformBrowser } from '@angular/common';
import { ChangeDetectorRef, Component,  Inject, Input, OnDestroy, OnInit, Optional, PLATFORM_ID, Renderer2 } from '@angular/core';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { debounceTime, Observable, filter, share, startWith, Subject, takeUntil, switchMap } from 'rxjs';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { FormControl } from '@angular/forms';
import { HotelFiltersService } from '@app/services/hotelfilters.service';
import { ParamFilters } from '@app/models/filtermodels/ParamFilters';
import { Reil } from '@app/models/hotels/Reil';
import { TrafficsService } from '@app/services/traffics.service';
import { AuthGuardService } from '@app/services/auth-guard.service';
import { Partner } from '@app/models/partner/IPartner';
import { MEDIA_TOKEN } from '../../../../../../tockens';

@Component({
    selector: 'clever-reil',
    templateUrl: './reil.component.html',
    styleUrls: ['./reil.component.scss'],
    animations: [
        trigger('zoom', [
            state('leftBottom', style({ transform: 'scale(0.3)', transformOrigin: 'left bottom', opacity: '0' })),
            state('default', style({ transform: 'scale(1)', opacity: '1' })),
            transition('* <=> default', animate('10ms cubic-bezier(0.25,1,.25,1)'))
        ])
    ],
    standalone: false
})
export class ReilComponent implements OnInit, OnDestroy {
    @Input() mode: string;
    protected unsubscribe$ = new Subject();
    isBrowser: boolean = false;
    showModal: boolean = false;
    focus: boolean = true;
    media: string = 'lg';
    origin = 'default';
    zielText = 'Alle';
    searchObj: Observable<any>;
    mySearchControl = new FormControl();
    selectedList: Reil[] = [];
    partner: Partner;
    ops: string = '';

    constructor(
        protected mediaObserver: BreakpointObserver,
        protected cdr: ChangeDetectorRef,
        protected trafficsService: TrafficsService,
        protected render: Renderer2,
        protected hotelService: HotelFiltersService,
        protected guardService: AuthGuardService,
        @Inject(PLATFORM_ID) protected platformId: any,
        @Optional() @Inject(MEDIA_TOKEN) private media_token: string
    ) {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.media = this.media_token;
    }

    ngOnInit(): void {
        this.initPartner();
        this.initMedia();
        this.initSearchChange();
        this.initFilterSub();
    }

    initPartner(): void {
        this.guardService.getPartner$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe(partner => {
            if (partner?.partnerid?.length > 0) {
                this.partner = partner;
                for (let i = 0; i < this.partner?.agenturen?.length; i++) {
                    if (this.partner?.agenturen[i]?.aktiv) {
                        this.ops += this.partner?.agenturen[i]?.code + ',';
                    }
                }
            }
        });
    }

    initSearchChange() {
        const filterShare = this.mySearchControl.valueChanges.pipe(
            startWith(''),
            debounceTime(100),
            filter(value => (typeof value === 'string' && value?.length > 0)),
            share()
        );

        this.searchObj = filterShare.pipe(
            switchMap(value => this.trafficsService.request({
                productType: 'railtravel',
                searchValue: value,
                subTypeTrainStation: true,
                tourOperatorList: this.ops,
                limit: 10
            }, 'completions'))
        );
    }

    initFilterSub() {
        this.hotelService.getfilters$.pipe(
            takeUntil(this.unsubscribe$)
        ).subscribe((filter: ParamFilters) => {
            if (filter?.airportsResult?.length > 0) {
                if (JSON.stringify(this.selectedList) !== JSON.stringify(filter?.airportsResult)) {
                    this.selectedList = JSON.parse(JSON.stringify(filter?.airportsResult));
                    this.zielText = this.selectedList.length + '';
                }
            } else {
                this.zielText = 'Alle';
            }
        });
    }

    initMedia() {
       if (this.mediaObserver.isMatched(Breakpoints.XSmall)) {
            this.media = 'xs';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Small)) {
            this.media = 'sm';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Medium)) {
            this.media = 'md';
        }
        if (this.mediaObserver.isMatched(Breakpoints.Large)) {
            this.media = 'lg';
        }
        if (this.mediaObserver.isMatched(Breakpoints.XLarge)) {
            this.media = 'xl';
        }
        this.cdr.detectChanges();
    }

    
    ngOnDestroy(): void {
        this.unsubscribe$.next(true);
        this.unsubscribe$.complete();
    }

    openModal(ev) {
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    openModalMobile(ev) {
        // this.render.addClass(document.body, 'model-open');
        ev?.stopPropagation();
        this.showModal = true;
        this.zoomin('leftBottom');
    }

    closeModal() {
        this.showModal = false;
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    closeModalMobile() {
        this.showModal = false;
        // this.render.removeClass(document.body, 'model-open');
        this.mySearchControl.setValue('');
        this.cdr.detectChanges();
    }

    zoomin(position: any) {
        this.origin = position;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.origin = 'default';
            this.cdr.detectChanges();
        });
    }

    clearText() {
        this.mySearchControl.setValue('');
        this.focus = false;
        this.cdr.detectChanges();
        setTimeout(() => {
            this.focus = true;
            this.cdr.detectChanges();
        }, 50);
    }

    selectValue(item) {
        this.selectedList.push({
            name: item.name,
            code: item.code,
        });
        this.zielText = this.selectedList.length + '';
    }

    isNotInlist(item): boolean {
        for (let i = 0; i < this.selectedList.length; i++) {
            if (this.selectedList[i].code === item?.code) {
                return false;
            }
        }
        return true;
    }

    removeAllSelected() {
        this.selectedList = [];
        this.zielText = 'Alle';
    }

    removeFromSelected(k) {
        if (this.selectedList[k]) {
            this.selectedList.splice(k, 1);
        }
        this.zielText = this.selectedList.length > 0 ? this.selectedList.length + '' : 'Alle';
    }

    take() {
        this.hotelService.airportsResult(JSON.parse(JSON.stringify(this.selectedList)), this.mode !== 'home');
        this.closeModal();
    }
}
